import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { FeatureSettingsService, ResidentSettings } from '../../services/feature-settings.service';
import { AuthService } from '../../auth/auth.service';
import {
  DEFAULT_SCENES, MAX_CUSTOM_SCENES, SCENES_KEY, SCENES_BLOB_VERSION, ScenesBlob, SceneDef,
} from '../interfaces/scenes.interface';

@Injectable({ providedIn: 'root' })
export class ScenesService {
  private writeChain: Promise<unknown> = Promise.resolve();

  constructor(
    private featureSettings: FeatureSettingsService,
    private mqtt: MqttConnectorService,
    private auth: AuthService,
  ) {}

  /** Effective, ordered scene list: defaults (with overrides applied) then custom by order. */
  getScenes(): Observable<SceneDef[]> {
    return this.featureSettings.getResidentSettings().pipe(
      filter((s): s is ResidentSettings => s !== null),
      map(settings => this.materialize((settings?.[SCENES_KEY] as ScenesBlob | undefined))),
    );
  }

  private materialize(blob?: ScenesBlob): SceneDef[] {
    const overrides = blob?.overrides ?? {};
    const custom = [...(blob?.custom ?? [])].sort((a, b) => a.order - b.order);
    const defaults = DEFAULT_SCENES.map(d => overrides[d.id] ? { ...overrides[d.id], isDefault: true } : d);
    return this.applyOrder([...defaults, ...custom], blob?.order);
  }

  /** Order the effective list by the saved global order; any scene not listed keeps its
   *  natural position at the end (so newly-created scenes appear last until reordered). */
  private applyOrder(scenes: SceneDef[], order?: string[]): SceneDef[] {
    if (!order || order.length === 0) { return scenes; }
    const rank = new Map(order.map((id, i) => [id, i]));
    const listed = scenes.filter(s => rank.has(s.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
    const unlisted = scenes.filter(s => !rank.has(s.id));
    return [...listed, ...unlisted];
  }

  async saveScene(def: SceneDef): Promise<boolean> {
    return this.mutate(blob => {
      if (def.isDefault) {
        blob.overrides[def.id] = def;
      } else {
        const idx = blob.custom.findIndex(c => c.id === def.id);
        if (idx >= 0) { blob.custom[idx] = def; }
        else {
          if (blob.custom.length >= MAX_CUSTOM_SCENES) {
            throw new Error(`Scene limit reached (${MAX_CUSTOM_SCENES}).`);
          }
          blob.custom.push(def);
        }
      }
      return blob;
    });
  }

  async deleteScene(id: string): Promise<boolean> {
    return this.mutate(blob => { blob.custom = blob.custom.filter(c => c.id !== id); return blob; });
  }

  async resetDefault(id: string): Promise<boolean> {
    return this.mutate(blob => { delete blob.overrides[id]; return blob; });
  }

  /** Persist a new global display order (scene ids — defaults and custom together). */
  async reorderScenes(orderedIds: string[]): Promise<boolean> {
    return this.mutate(blob => { blob.order = [...orderedIds]; return blob; });
  }

  /** One-shot lookup of an effective scene by id (defaults with overrides applied, then custom). */
  async getSceneById(id: string): Promise<SceneDef | undefined> {
    const scenes = await firstValueFrom(this.getScenes().pipe(take(1)));
    return scenes.find(s => s.id === id);
  }

  /** Serialized read-modify-write against a settled (non-null) settings blob. */
  private mutate(fn: (blob: ScenesBlob) => ScenesBlob): Promise<boolean> {
    const write = this.writeChain
      .catch(() => undefined)
      .then(async () => {
        const current = await firstValueFrom(
          this.featureSettings.getResidentSettings().pipe(
            filter((s): s is ResidentSettings => s !== null), take(1)));
        const prev = current?.[SCENES_KEY] as ScenesBlob | undefined;
        const blob: ScenesBlob = {
          version: SCENES_BLOB_VERSION,
          overrides: { ...(prev?.overrides ?? {}) },
          custom: [...(prev?.custom ?? [])],
          order: prev?.order ? [...prev.order] : undefined,
          rev: (prev?.rev ?? 0) + 1,
        };
        const next = fn(blob);
        const merged: ResidentSettings = { ...(current ?? {}), [SCENES_KEY]: next };
        const saved = await this.featureSettings.updateResidentSettings(merged);

        // Only push once the cloud write actually landed. Pushing after a failed
        // write leaves the unit ahead of the cloud, and reconcile treats unit-ahead
        // as a no-op — the scene would be stranded on the hub forever.
        if (saved) {
          // Fire-and-forget: a unit that is offline must not fail the save. The
          // rev comparison on next start pushes whatever the unit missed.
          this.pushToUnit(next).catch(() => undefined);
        }

        return saved;
      });
    this.writeChain = write;
    return write;
  }

  /** Send the materialized scene list to the unit. Resolves false on any failure. */
  async pushToUnit(blob?: ScenesBlob): Promise<boolean> {
    let cognitoSub: string;
    try {
      cognitoSub = await this.auth.getValidCognitoUserId();
    } catch {
      return false; // no valid session; nothing to push
    }

    const source = blob ?? (await firstValueFrom(
      this.featureSettings.getResidentSettings().pipe(
        filter((s): s is ResidentSettings => s !== null), take(1))))?.[SCENES_KEY] as ScenesBlob | undefined;

    try {
      await firstValueFrom(this.mqtt.publishApiCommand('save_resident_scenes', {
        cognito_sub: cognitoSub,
        rev: source?.rev ?? 0,
        scenes: this.materialize(source),
      }));
      return true;
    } catch {
      return false;
    }
  }

  /** Compare the local rev against the unit's and push if the unit is stale.
   *  A unit that is AHEAD is left alone in v1: another device wrote it, and
   *  because Task 7b makes the DynamoDB write upsert, that device also wrote
   *  the cloud copy — so this app catches up through the normal settings read.
   *  Without Task 7b this branch would silently strand scenes on the unit.
   *
   *  `unitHasNoEntry` (from get_resident_scenes' empty-scenes signal) forces a
   *  push even when the numbers alone say otherwise: a blob saved before revs
   *  existed reads as rev 0 locally, which is indistinguishable from a unit
   *  rev of 0 by number alone — but the unit has genuinely never seen this
   *  resident, so `localRev > unitRev` (0 > 0) would wrongly say in-sync and
   *  the resident's real scenes would never reach the unit. */
  async reconcileWithUnit(unitRev: number, unitHasNoEntry = false): Promise<'pushed' | 'behind' | 'in-sync'> {
    const blob = (await firstValueFrom(
      this.featureSettings.getResidentSettings().pipe(
        filter((s): s is ResidentSettings => s !== null), take(1))))?.[SCENES_KEY] as ScenesBlob | undefined;
    const localRev = blob?.rev ?? 0;

    if (unitHasNoEntry) { await this.pushToUnit(blob); return 'pushed'; }
    if (localRev > unitRev) { await this.pushToUnit(blob); return 'pushed'; }
    if (localRev < unitRev) { return 'behind'; }
    return 'in-sync';
  }

  /** Build the local blob shape from a get_resident_scenes response, or null
   *  if the unit has no scenes for this resident. The unit stores the
   *  materialized list; this splits it back for local storage. A restored
   *  default-id scene is re-stamped isDefault: true here because materialize()
   *  sets that flag on the way out and the split must put it back.
   *
   *  A default-id scene that is deep-equal to its DEFAULT_SCENES entry is left
   *  out of `overrides`: the resident never touched it, so storing it as an
   *  override would freeze it against future code changes to that default
   *  (materialize() always prefers an override when one is present). */
  private buildRestoredBlob(response: any): ScenesBlob | null {
    const scenes: SceneDef[] = response?.scenes ?? [];
    if (!scenes.length) { return null; }

    const defaultIds = new Set(DEFAULT_SCENES.map(d => d.id));
    const defaultById = new Map(DEFAULT_SCENES.map(d => [d.id, d]));
    const overrides = scenes
      .filter(s => defaultIds.has(s.id))
      .map(s => ({ ...s, isDefault: true }))
      .filter(s => JSON.stringify(s) !== JSON.stringify(defaultById.get(s.id)))
      .reduce((acc, s) => ({ ...acc, [s.id]: s }), {});

    return {
      version: SCENES_BLOB_VERSION,
      overrides,
      custom: scenes.filter(s => !defaultIds.has(s.id)),
      order: scenes.map(s => s.id),
      rev: response?.rev ?? 0,
    };
  }

  /** Seed an empty local blob from the unit — new device, reinstall, cleared
   *  cache. Never overwrites scenes the resident already has locally.
   *
   *  Pass a get_resident_scenes response already fetched by the caller (e.g.
   *  syncWithUnit) to skip fetching a second one; omit it to fetch fresh. */
  async restoreFromUnit(prefetchedResponse?: any): Promise<boolean> {
    const current = await firstValueFrom(
      this.featureSettings.getResidentSettings().pipe(
        filter((s): s is ResidentSettings => s !== null), take(1)));
    const blob = current?.[SCENES_KEY] as ScenesBlob | undefined;
    if ((blob?.custom?.length ?? 0) > 0 || Object.keys(blob?.overrides ?? {}).length > 0) {
      return false;
    }

    let response = prefetchedResponse;
    if (response === undefined) {
      let cognitoSub: string;
      try { cognitoSub = await this.auth.getValidCognitoUserId(); } catch { return false; }

      try {
        response = await this.mqtt.requestApiCommand('get_resident_scenes', { cognito_sub: cognitoSub });
      } catch {
        return false; // unit unreachable or slow; DynamoDB remains the restore path
      }
    }

    const restored = this.buildRestoredBlob(response);
    if (!restored) { return false; }
    return this.featureSettings.updateResidentSettings({ ...(current ?? {}), [SCENES_KEY]: restored });
  }

  /** Startup sync: restore if this device has nothing, otherwise reconcile.
   *  Never throws — a unit that is unreachable must not break app launch.
   *
   *  A single get_resident_scenes call — scoped to this resident's own
   *  cognito_sub — serves both branches. The old implementation called the
   *  unit-wide `status` action first to read a rev, which (a) merged every
   *  co-resident's cognito sub and rev into one per-unit-topic response any
   *  co-resident could read, (b) ran the expensive, blocking status builder
   *  (df, supervisor round trips, network enumeration) just to read one
   *  integer, and (c) took scene sync down whenever status failed for
   *  unrelated reasons. */
  async syncWithUnit(): Promise<'restored' | 'pushed' | 'behind' | 'in-sync' | 'unavailable'> {
    let cognitoSub: string;
    try {
      cognitoSub = await this.auth.getValidCognitoUserId();
    } catch {
      return 'unavailable';
    }

    let response: any;
    try {
      response = await this.mqtt.requestApiCommand('get_resident_scenes', { cognito_sub: cognitoSub });
    } catch {
      return 'unavailable';
    }

    try {
      if (await this.restoreFromUnit(response)) { return 'restored'; }
      const unitHasNoEntry = (response?.scenes?.length ?? 0) === 0;
      return await this.reconcileWithUnit(response?.rev ?? 0, unitHasNoEntry);
    } catch {
      return 'unavailable'; // an unguarded settings read (e.g. a propagated GraphQL error) must
                             // not turn app launch into an unhandled rejection.
    }
  }
}
